import { of, concat, from } from 'rxjs';
import { mergeMap, map, catchError, filter, switchMap, debounceTime, withLatestFrom } from 'rxjs/operators';
import { combineEpics, ofType } from 'redux-observable';
import {
  bowlerSet,
  bowlerError,
  bowlerHandleForm,
  bowlersListLoad,
  bowlersListLoaded,
  bowlerSavePhoto,
  bowlerPhotoSaved,
  types,
  pages,
  bowlerSelect,
} from './bowlers.redux';
import { sidebarMenuItemSelected } from 'components/sideBar/sideBar.redux';
import { types as spectreAppTypes } from 'components/spectreApp/spectreApp.redux';
import { datasources } from '../db/datasources';
import { takePhoto } from 'components/fileSystem/takePhoto';
import { galleryPhoto } from 'components/fileSystem/galleryPhoto';
import { fileSystem, isDesktop } from 'components/fileSystem';
import uuid from 'uuid/v1';
import * as R from 'ramda';
import * as Promise from 'bluebird';

const bowlersHandleFormEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.bowlerHandleForm),
    debounceTime(500),
    mergeMap(({ payload: { bowler } }) => {
      const { customerDS, customerProshopDS } = datasources;
      if (bowler.id) {
        return customerDS.update({ ...bowler, localId: bowler.id }).then(() => bowlerSet({ bowler }));
      } else {
        const id = uuid();
        return Promise.all([
          customerDS.create({ ...bowler, id }),
          customerProshopDS.create({
            id: uuid(),
            customer_id: id,
            proshop_id: state$.value.spectreApp.userSession.currentProshop,
          }),
        ]).then(() => {
          return bowlerSet({ bowler: { ...bowler, id } });
        });
      }
    })
  );

const bowlersRemoveEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.bowlerRemove),
    mergeMap(({ payload: { bowler } }) => {
      const { customerDS, customerProshopDS, ballDrilledDS, specSheetDS } = datasources;
      return from(
        Promise.all([
          customerDS.delete(bowler.id),
          customerProshopDS.deleteFromFields({
            customer_id: bowler.id,
            proshop_id: state$.value.spectreApp.userSession.currentProshop,
          }),
          ballDrilledDS.deleteFromFields({
            customer_id: bowler.id,
          }),
          specSheetDS.deleteFromFields({
            customer_id: bowler.id,
          }),
        ])
      ).pipe(
        switchMap(() => {
          const { bowlers } = state$.value.bowlers;
          const filterBowlers = bowlers.filter(bowlerTemp => bowlerTemp.id !== bowler.id);
          if (filterBowlers.length > 0) {
            return concat(
              of(
                bowlerSelect({
                  bowler: {
                    ...filterBowlers[0],
                  },
                  noChangePage: true,
                })
              ),
              of(bowlersListLoad())
            );
          } else {
            return concat(
              of(
                bowlerSelect({
                  bowler: {},
                  noChangePage: true,
                })
              ),
              of(bowlersListLoad())
            );
          }
        })
      );
    })
  );

const bowlersOpenPageEpic = (action$, state$) =>
  action$.pipe(
    ofType(spectreAppTypes.spectreappCurrentPage),
    filter(({ payload: { currentPage } }) => currentPage === 'bowlers'),
    withLatestFrom(state$),
    mergeMap(() => of(bowlersListLoad()))
  );

const bowlersSignatureEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.bowlerSetSignature),
    mergeMap(() => {
      return of(bowlerHandleForm({ bowler: state$.value.bowlers.bowler }));
    })
  );

const bowlersPhotoSavedEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.bowlerPhotoSaved),
    mergeMap(() => {
      return of(bowlerHandleForm({ bowler: state$.value.bowlers.bowler }));
    })
  );

const comparatorCaseInsensitive = property =>
  R.compose(
    R.toLower,
    R.prop(property)
  );

const sortByLastnameAndFirstnameCaseInsensitive = R.sortWith([
  R.ascend(comparatorCaseInsensitive('lastname')),
  R.ascend(comparatorCaseInsensitive('firstname')),
]);

const bowlersListLoadedEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.bowlersListLoad),
    switchMap(() => {
      const { customerDS } = datasources;
      return from(
        customerDS.read({
          proshopId: state$.value.spectreApp.userSession.currentProshop,
        })
      ).pipe(
        switchMap(data => {
          if (!state$.value.bowlers.bowler.id) {
            return concat(
              of(
                bowlerSelect({
                  bowler: R.defaultTo({})(
                    data.find(bowler => bowler.id === state$.value.spectreApp.lastCustomerInfo.lastCustomer)
                  ),
                  noChangePage: true,
                })
              ),
              of(
                bowlersListLoaded({
                  bowlers: sortByLastnameAndFirstnameCaseInsensitive(data),
                })
              )
            );
          } else {
            return of(
              bowlersListLoaded({
                bowlers: sortByLastnameAndFirstnameCaseInsensitive(data),
              })
            );
          }
        })
      );
    })
  );

const bowlersEpic = action$ =>
  action$.pipe(
    ofType(types.bowlersChangePage),
    filter(action => action.payload.page === pages.list),
    switchMap(() => concat(of(bowlersListLoad()), of(sidebarMenuItemSelected({ menuItemSelected: '1' }))))
  );

const bowlersStartApplicationEpic = (action$, state$) =>
  action$.pipe(
    ofType(spectreAppTypes.spectreappReady),
    withLatestFrom(state$),
    mergeMap(() => of(bowlersListLoad()))
  );

const bowlersAddNewEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.bowlerAddNew),
    mergeMap(() =>
      from(
        new Promise((success, reject) => {
          const { customerDS, customerProshopDS } = datasources;

          const customerId = uuid();

          customerDS
            .create({ localId: customerId, hand: state$.value.settings.appSettings.default_hand })
            .then(
              customer =>
                new Promise(success =>
                  customerProshopDS
                    .create({
                      customer_id: customerId,
                      proshop_id: state$.value.spectreApp.userSession.currentProshop,
                    })
                    .then(() => success(customer))
                )
            )
            .then(customer => {
              success(customer);
            })
            .catch(error => {
              console.warn(error);
              reject(error);
            });
        })
      ).pipe(
        map(customer => {
          return bowlerSelect({ bowler: customer });
        }),
        catchError(error => {
          console.warn(error);
          return bowlerError({ error: {} });
        })
      )
    )
  );

const bowlersTakePhotoEpic = action$ =>
  action$.pipe(
    ofType(types.bowlerTakePhoto),
    mergeMap(() =>
      from(takePhoto()).pipe(
        map(photoData => {
          return bowlerSavePhoto({ photoData });
        })
      )
    )
  );

const bowlersGalleryPhotoEpic = action$ =>
  action$.pipe(
    ofType(types.bowlerGalleryPhoto),
    mergeMap(() =>
      from(galleryPhoto()).pipe(
        map(photoData => {
          return bowlerSavePhoto({ photoData });
        })
      )
    )
  );

const bowlersRemovePhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.bowlerRemovePhoto),
    mergeMap(() => of(bowlerHandleForm({ bowler: state$.value.bowlers.bowler })))
  );

const bowlersSavePhotoEpic = (action$, state$) =>
  action$.pipe(
    ofType(types.bowlerSavePhoto),
    withLatestFrom(state$),
    switchMap(([{ payload: { photoData: { realData, dataType } } }, state]) =>
      from(
        new Promise(success => {
          const { bowler } = state.bowlers;
          const { savebase64AsImageFile, getImageUrl } = fileSystem();

          let fileName = 'customer_' + bowler.id + '_' + uuid() + '.png';

          savebase64AsImageFile(fileName, realData, dataType).then(file => {
            if (isDesktop()) {
              file.url = getImageUrl(file.url).replace('cdvfile:', 'electronfile:');
            }
            success(file);
          });
        })
      ).pipe(
        map(photo => {
          return bowlerPhotoSaved({ photo: photo.url });
        })
      )
    )
  );

export default combineEpics(
  bowlersEpic,
  bowlersStartApplicationEpic,
  bowlersListLoadedEpic,
  bowlersHandleFormEpic,
  bowlersSignatureEpic,
  bowlersAddNewEpic,
  bowlersTakePhotoEpic,
  bowlersGalleryPhotoEpic,
  bowlersSavePhotoEpic,
  bowlersPhotoSavedEpic,
  bowlersRemovePhotoEpic,
  bowlersRemoveEpic,
  bowlersOpenPageEpic
);
